/* eslint-disable react/prop-types */
/* eslint-disable no-undef */
import React, { useEffect, useRef, useState } from "react";
import { Chart, registerables } from "chart.js";

// Register Chart.js components
Chart.register(...registerables);

/**
 * MyopiaPredictiveGraphModal component for selecting treatment curves
 * @param {Object} props - Component props
 * @param {Function} props.closePopup - Function to close the modal
 * @param {Function} props.onGenerateReport - Function called with selected items when generating report
 * @param {Array} props.predictiveData - Predictive curve data
 * @param {Object} props.currentRef - Current reference data {age, l, r}
 */
const MyopiaPredictiveGraphModal = ({
  closePopup,
  onGenerateReport,
  predictiveData,
  currentRef
}) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  // Legend items with their corresponding treatment mod values
  // Order matches the Kotlin legend: Real Patient Data, 70%, 40%, 50%, 60%, 30%, 0%
  const legendItems = [
    { label: "Real Patient Data", mod: "real", color: "#EC6C84" },
    {
      label: "Defocus Segment Spectacle lens",
      mod: 70,
      color: "#74140B"
    },
    {
      label: "Anti-Myopia Orthokeratology",
      mod: 40,
      color: "#F1A253"
    },
    { label: "Atropine 0.025%", mod: 50, color: "#F6CE69" },
    {
      label: "Anti-Myopia soft contacts",
      mod: 60,
      color: "#6BBDBE"
    },
    { label: "Atropine 0.01%", mod: 30, color: "#569FE4" },
    { label: "No treatment", mod: 0, color: "#C8CACE" }
  ];

  // Initialize all items as selected
  const [selectedItems, setSelectedItems] = useState(
    legendItems.reduce((acc, item) => {
      acc[item.mod] = true;
      return acc;
    }, {})
  );

  // Calculate progression using MyopiaProgressionCalculator logic
  const DEFAULT_PROG = -0.04;
  const MIN_PROGRESSION_AGE = 5;
  const PROG_CONST_X3 = 0.0022;
  const PROG_CONST_X2 = -0.0403;
  const PROG_CONST_X1 = 0.2859;
  const PROG_CONST_X0 = -1.3244;

  const PROG_TABLE = [
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    0.0,
    -1.0766,
    -0.8962,
    -0.77,
    -0.6848,
    -0.6274,
    -0.5846,
    -0.5432,
    -0.49,
    -0.4118,
    -0.2954,
    -0.24,
    -0.2,
    -0.16,
    -0.13,
    -0.1,
    -0.08,
    -0.06,
    -0.05
  ];

  const getProgressionInternal = (age, slowing) => {
    if (age > 15) {
      if (age >= PROG_TABLE.length) return slowing * DEFAULT_PROG;
      return slowing * PROG_TABLE[age];
    }
    if (age < MIN_PROGRESSION_AGE) return 0.0;
    const x = age - 5;
    return (
      slowing *
      (Math.pow(x, 3) * PROG_CONST_X3 +
        Math.pow(x, 2) * PROG_CONST_X2 +
        x * PROG_CONST_X1 +
        PROG_CONST_X0)
    );
  };

  const getPrescriptionAtAge = (
    startPrescription,
    startAge,
    curAge,
    slowing,
    treatmentAccelMultiplier,
    patientRateMultiplier
  ) => {
    let newPrescription = startPrescription;
    if (startAge === curAge) return startPrescription;

    for (let age = startAge; age < curAge; age++) {
      if (age < MIN_PROGRESSION_AGE) {
        newPrescription += 0.0;
      } else if (age < PROG_TABLE.length) {
        newPrescription +=
          getProgressionInternal(age, slowing) *
          treatmentAccelMultiplier *
          patientRateMultiplier;
      } else {
        newPrescription += slowing * DEFAULT_PROG;
      }
    }
    return newPrescription;
  };

  const getTreatmentColor = mod => {
    const colors = {
      0: "#C8CACE", // Grey - No treatment
      30: "#569FE4", // Sky blue - Atropine 0.01%
      40: "#F1A253", // Orange/tan - Anti-Myopia Orthokeratology
      50: "#F6CE69", // Light yellow - Atropine 0.025%
      60: "#6BBDBE", // Teal - Anti-Myopia soft contacts
      70: "#74140B" // Dark reddish-brown - Defocus Segment Spectacle lens
    };
    return colors[mod] || "#CCCCCC";
  };

  useEffect(() => {
    if (!canvasRef.current || !predictiveData || predictiveData.length === 0) {
      return;
    }

    // Destroy existing chart if it exists
    if (chartRef.current) {
      chartRef.current.destroy();
    }

    const byAge = [...predictiveData].sort((a, b) => a.age - b.age);
    const rdAges = [...new Set(predictiveData.map(d => d.age))].sort(
      (a, b) => a - b
    );

    // Use current exam as reference when currentRef is provided
    let prescriptionAge = rdAges.length > 0 ? Math.min(...rdAges) : 6;
    let useL = false;
    let startPrescription = 0;

    if (currentRef && typeof currentRef.age === "number") {
      prescriptionAge = currentRef.age;
      if (currentRef.l != null && currentRef.r != null) {
        useL = currentRef.l < currentRef.r;
      } else {
        useL = currentRef.l != null;
      }
      startPrescription = (useL ? currentRef.l : currentRef.r) ?? 0;
    } else {
      const startData = byAge[0];
      const last = predictiveData[predictiveData.length - 1];
      const left = last.l ?? 0;
      const right = last.r ?? 0;
      useL = left < right;
      startPrescription = (useL ? startData.l : startData.r) ?? 0;
    }

    const rate = (predictiveData[predictiveData.length - 1] || {}).rate ?? 1.0;

    // Real patient data - filter out null/undefined values, ensure unique ages, and sort
    const seenAges = new Set();
    const realData = byAge
      .filter(rd => {
        const value = useL ? rd.l : rd.r;
        if (value === null || value === undefined) return false;
        if (seenAges.has(rd.age)) return false; // Skip duplicate ages
        seenAges.add(rd.age);
        return true;
      })
      .map(rd => ({
        x: rd.age,
        y: (useL ? rd.l : rd.r) ?? 0
      }))
      .sort((a, b) => a.x - b.x); // Ensure sorted by age

    const maxAge = Math.min(17, prescriptionAge + 10);
    const ageRange = Array.from(
      { length: maxAge - prescriptionAge + 1 },
      (_, i) => prescriptionAge + i
    );

    // Treatment datasets
    // Treatment curves start from prescriptionAge (current exam point)
    const treatments = [30, 40, 50, 60, 70, 0];
    const treatmentDatasets = treatments
      .filter(mod => selectedItems[mod]) // Only include selected treatments
      .map(mod => {
        const slowing = mod === 0 ? 1.0 : (100.0 - mod) / 100.0;
        const label = mod === 0 ? "No Treatment" : `${mod}%`;
        const data = ageRange.map(age => ({
          x: age,
          y:
            Math.round(
              getPrescriptionAtAge(
                startPrescription,
                prescriptionAge,
                age,
                slowing,
                1.0,
                rate
              ) * 100
            ) / 100.0
        }));
        return {
          label,
          data,
          borderWidth: 2,
          borderColor: getTreatmentColor(mod),
          backgroundColor: getTreatmentColor(mod),
          fill: false,
          pointRadius: context => {
            // Hide point at the starting age (prescriptionAge) so it connects to real data
            return context.parsed.x === prescriptionAge ? 0 : 4;
          },
          pointBackgroundColor: getTreatmentColor(mod),
          pointBorderColor: getTreatmentColor(mod),
          pointBorderWidth: 1
        };
      });

    // Build datasets array - Real Patient Data first if selected
    const datasets = [];
    if (selectedItems.real && realData.length > 0) {
      datasets.push({
        label: "Real Patient Data",
        data: realData,
        borderWidth: 3,
        borderColor: "#EC6C84",
        fill: false,
        pointRadius: realData.length === 1 ? 4 : 0, // Show point if only one data point
        pointBackgroundColor: "#EC6C84",
        pointBorderColor: "#EC6C84",
        pointBorderWidth: 2
      });
    }
    datasets.push(...treatmentDatasets);

    // Create chart
    const chart = new Chart(canvasRef.current, {
      type: "line",
      data: {
        datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: true
          }
        },
        scales: {
          x: {
            type: "linear",
            position: "bottom",
            min: prescriptionAge,
            max: maxAge,
            title: {
              display: true,
              text: "Age (years)",
              font: { size: 16 }
            },
            ticks: {
              font: { size: 12 },
              stepSize: 1
            },
            grid: {
              display: true,
              color: "rgba(0,0,0,0.12)"
            }
          },
          y: {
            reverse: true,
            title: {
              display: true,
              text: "Refractive Error (D)",
              font: { size: 16 }
            },
            ticks: { font: { size: 12 } },
            grid: {
              display: true,
              color: "rgba(0,0,0,0.12)"
            }
          }
        }
      }
    });

    // Draw eye label on last point of Real Patient Data if visible
    if (selectedItems.real && realData.length > 0) {
      setTimeout(() => {
        const ctx = chart.ctx;
        const idxReal = chart.data.datasets.findIndex(
          ds => ds.label === "Real Patient Data"
        );
        if (idxReal >= 0) {
          const meta = chart.getDatasetMeta(idxReal);
          const last = meta.data[meta.data.length - 1];
          if (last) {
            const x = last.x;
            const y = last.y;
            const radius = 14;
            ctx.fillStyle = "#FFFFFF";
            ctx.beginPath();
            ctx.arc(x, y, radius, 0, 2 * Math.PI);
            ctx.fill();
            ctx.strokeStyle = "#EC6C84";
            ctx.lineWidth = 2.5;
            ctx.beginPath();
            ctx.arc(x, y, radius, 0, 2 * Math.PI);
            ctx.stroke();
            ctx.fillStyle = "#000000";
            ctx.font = "bold 11px Arial";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            const eyeLabel = useL ? "LE" : "RE";
            ctx.fillText(eyeLabel, x, y);
          }
        }
      }, 100);
    }

    chartRef.current = chart;

    // Cleanup function
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [predictiveData, currentRef, selectedItems]);

  const handleCheckboxChange = mod => {
    setSelectedItems(prev => ({
      ...prev,
      [mod]: !prev[mod]
    }));
  };

  const handleGenerateReport = () => {
    // Get selected treatment mods (excluding "real" which is always included if selected)
    const selectedTreatments = legendItems
      .filter(item => item.mod !== "real" && selectedItems[item.mod])
      .map(item => item.mod);

    const selectionData = {
      includeRealPatientData: selectedItems.real,
      selectedTreatments: selectedTreatments
    };

    // Close the modal immediately
    closePopup();

    // Then call the generate report function with predictive data and currentRef
    onGenerateReport(selectionData, predictiveData, currentRef);
  };

  return (
    <div className="custommodal">
      <div className="custommodalinner myopia-predictive-graph-modal">
        <div className="firstpage">
          <div className="hrpadding">
            <h4 className="modal-heading">Select Treatment Curves</h4>
            <button className="cancle" onClick={closePopup}>
              <span className="cross">✕</span>
            </button>
          </div>

          <div className="myopia-graph-content">
            <div className="myopia-graph-wrapper">
              <div className="myopia-graph-container">
                <canvas ref={canvasRef} style={{ maxHeight: "500px" }}></canvas>
              </div>

              <div className="myopia-legend-container">
                <h5 className="legend-title">Legend</h5>
                <div className="legend-items">
                  {legendItems.map(item => (
                    <div key={item.mod} className="legend-item">
                      <label className="legend-checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedItems[item.mod] || false}
                          onChange={() => handleCheckboxChange(item.mod)}
                          className="legend-checkbox"
                        />
                        <span
                          className="legend-color-indicator"
                          style={{ backgroundColor: item.color }}
                        ></span>
                        <span className="legend-label-text">{item.label}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="myopia-graph-actions">
              <button
                className="loginbtn1 proceed-btn"
                onClick={handleGenerateReport}
              >
                Generate Report
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyopiaPredictiveGraphModal;
